import React from 'react';
import {
    Alert,
    Box,
    CircularProgress,
    Divider,
    FormControlLabel,
    Stack,
    Switch,
    Typography,
} from '@mui/material';
import { useAlert } from '../core/AlertContext';
import { useModuleSettings, useUpdateModuleSettings } from '../hooks/moduleSettings';
import { ModuleKey } from '../utils/moduleEnabled';

interface ModuleRow {
    key: ModuleKey;
    label: string;
    description: string;
}

const MODULE_ROWS: ModuleRow[] = [
    { key: 'hutbook', label: 'Hüttenbuch', description: 'Ein- und Austragungen im Hüttenbuch.' },
    { key: 'meetings', label: 'Meeting', description: 'Online-Konferenzen (Jitsi Meet).' },
    { key: 'blog', label: 'Blog', description: 'Beiträge, Kommentare und Reaktionen.' },
    { key: 'storage', label: 'Datenablage', description: 'Ordner- und Dateiverwaltung.' },
    { key: 'info', label: 'Info', description: 'Info-Seiten (Ämter, Bank, Anschrift).' },
];

const ModuleSettings: React.FC = () => {
    const { showAlert } = useAlert();
    const { modules, isLoading, isError } = useModuleSettings();
    const { updateModules, isUpdatingModules } = useUpdateModuleSettings();

    const handleToggle = async (key: ModuleKey, checked: boolean) => {
        try {
            await updateModules({ [key]: checked });
            showAlert({ text: checked ? 'Modul aktiviert.' : 'Modul deaktiviert.' });
        } catch {
            showAlert({ severity: 'error', text: 'Speichern fehlgeschlagen.' });
        }
    };

    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (isError || !modules) {
        return (
            <Alert severity="error">
                Die Funktions-Einstellungen konnten nicht geladen werden. Bitte später erneut versuchen.
            </Alert>
        );
    }

    return (
        <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                Funktionen
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Lege fest, welche optionalen Module in der Verbindung aktiv sind. Deaktivierte Module
                verschwinden aus Navigation und Dashboard; ihre Daten bleiben erhalten und sind nach
                erneuter Aktivierung wieder verfügbar.
            </Typography>

            <Stack divider={<Divider flexItem />} spacing={1.5}>
                {MODULE_ROWS.map((row) => (
                    <Box
                        key={row.key}
                        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}
                    >
                        <Box sx={{ minWidth: 0 }}>
                            <Typography sx={{ fontWeight: 600 }}>{row.label}</Typography>
                            <Typography variant="body2" color="text.secondary">
                                {row.description}
                            </Typography>
                        </Box>
                        <FormControlLabel
                            sx={{ m: 0 }}
                            control={
                                <Switch
                                    checked={modules[row.key]}
                                    disabled={isUpdatingModules}
                                    onChange={(event) => handleToggle(row.key, event.target.checked)}
                                    inputProps={{ 'aria-label': `${row.label} aktiv` }}
                                />
                            }
                            label={modules[row.key] ? 'Aktiv' : 'Inaktiv'}
                            labelPlacement="start"
                        />
                    </Box>
                ))}
            </Stack>
        </Box>
    );
};

export default ModuleSettings;
